/**
 * Daily Challenge Store (Zustand)
 */

import { create } from 'zustand';
import type { DailyQuestion, DailyInfo, DailySubmitResult, DailyAnswer } from '../lib/daily-api';
import { fetchDailyChallenge, submitDailyChallenge } from '../lib/daily-api';

type DailyScreen = 'loading' | 'start' | 'playing' | 'feedback' | 'finished' | 'already_played';

interface DailyState {
  // Screen
  screen: DailyScreen;
  
  // Daily info
  date: string | null;
  questions: DailyQuestion[];
  currentIndex: number;
  
  // Answers
  answers: DailyAnswer[];
  selectedAnswerIndex: number | null;
  answerStartTime: number | null;
  
  // Current question feedback
  lastCorrect: boolean | null;
  correctIndex: number | null;
  
  // Timer
  timeLeft: number;
  timerInterval: number | null;
  
  // Result
  result: DailySubmitResult | null;
  previousResult: DailyInfo['previousResult'] | null;
  
  // Loading
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  
  // Actions
  loadDaily: () => Promise<void>;
  startGame: () => void;
  selectAnswer: (index: number) => void;
  timeUp: () => void;
  nextQuestion: () => void;
  reset: () => void;
}

const TIME_PER_QUESTION = 20; // seconds

export const useDailyStore = create<DailyState>((set, get) => ({
  // Initial state
  screen: 'loading',
  date: null,
  questions: [],
  currentIndex: 0,
  answers: [],
  selectedAnswerIndex: null,
  answerStartTime: null,
  lastCorrect: null,
  correctIndex: null,
  timeLeft: TIME_PER_QUESTION,
  timerInterval: null,
  result: null,
  previousResult: null,
  isLoading: false,
  isSubmitting: false,
  error: null,
  
  // Actions
  loadDaily: async () => {
    set({ isLoading: true, error: null, screen: 'loading' });
    
    try {
      const daily = await fetchDailyChallenge();
      
      if (daily.alreadyPlayed) {
        set({
          screen: 'already_played',
          date: daily.date,
          previousResult: daily.previousResult || null,
          isLoading: false,
        });
      } else {
        set({
          screen: 'start',
          date: daily.date,
          questions: daily.questions,
          previousResult: null,
          isLoading: false,
        });
      }
    } catch (err) {
      set({ 
        error: 'Не удалось загрузить Daily Challenge', 
        isLoading: false,
        screen: 'start',
      });
    }
  },
  
  startGame: () => {
    const { questions } = get();
    if (questions.length === 0) return;
    
    // Start timer
    const interval = window.setInterval(() => {
      const { timeLeft, screen } = get();
      if (screen !== 'playing') {
        clearInterval(interval);
        return;
      }
      
      if (timeLeft <= 1) {
        get().timeUp();
      } else {
        set({ timeLeft: timeLeft - 1 });
      }
    }, 1000);
    
    set({
      screen: 'playing',
      currentIndex: 0,
      answers: [],
      selectedAnswerIndex: null,
      answerStartTime: Date.now(),
      timeLeft: TIME_PER_QUESTION,
      timerInterval: interval,
      lastCorrect: null,
      correctIndex: null,
    });
  },
  
  selectAnswer: (index: number) => {
    const { questions, currentIndex, answerStartTime, selectedAnswerIndex, timerInterval } = get();
    
    // Already answered
    if (selectedAnswerIndex !== null) return;
    
    const question = questions[currentIndex];
    if (!question) return;
    
    const timeSpent = answerStartTime ? Date.now() - answerStartTime : 0;
    
    // Stop timer
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    
    // Add answer
    const answer: DailyAnswer = {
      questionId: question.id,
      answerIndex: index,
      timeSpent,
    };
    
    set(state => ({
      answers: [...state.answers, answer],
      selectedAnswerIndex: index,
      screen: 'feedback',
      timerInterval: null,
    }));
    
    // Auto advance after 1.5s
    setTimeout(() => {
      get().nextQuestion();
    }, 1500);
  },
  
  timeUp: () => {
    const { questions, currentIndex, timerInterval } = get();
    
    // Stop timer
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    
    const question = questions[currentIndex];
    if (!question) return;
    
    // Add empty answer (time expired)
    const answer: DailyAnswer = {
      questionId: question.id,
      answerIndex: -1, // No answer
      timeSpent: TIME_PER_QUESTION * 1000,
    };
    
    set(state => ({
      answers: [...state.answers, answer],
      selectedAnswerIndex: -1,
      screen: 'feedback',
      timerInterval: null,
    }));
    
    // Auto advance after 1.5s
    setTimeout(() => {
      get().nextQuestion();
    }, 1500);
  },
  
  nextQuestion: async () => {
    const { questions, currentIndex, answers } = get();
    const nextIndex = currentIndex + 1;
    
    if (nextIndex >= questions.length) {
      // All questions answered - submit
      set({ isSubmitting: true });
      
      try {
        const result = await submitDailyChallenge(answers);
        set({
          screen: 'finished',
          result,
          isSubmitting: false,
        });
      } catch (err) {
        set({
          error: 'Ошибка отправки результатов',
          isSubmitting: false,
          screen: 'finished',
        });
      }
    } else {
      // Next question
      const interval = window.setInterval(() => {
        const { timeLeft, screen } = get();
        if (screen !== 'playing') {
          clearInterval(interval);
          return;
        }
        
        if (timeLeft <= 1) {
          get().timeUp();
        } else {
          set({ timeLeft: timeLeft - 1 });
        }
      }, 1000);
      
      set({
        currentIndex: nextIndex,
        screen: 'playing',
        selectedAnswerIndex: null,
        answerStartTime: Date.now(),
        timeLeft: TIME_PER_QUESTION,
        timerInterval: interval,
        lastCorrect: null,
        correctIndex: null,
      });
    }
  },
  
  reset: () => {
    const { timerInterval } = get();
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    
    set({
      screen: 'loading',
      date: null,
      questions: [],
      currentIndex: 0,
      answers: [],
      selectedAnswerIndex: null,
      answerStartTime: null,
      lastCorrect: null,
      correctIndex: null,
      timeLeft: TIME_PER_QUESTION,
      timerInterval: null,
      result: null,
      previousResult: null,
      error: null,
    });
  },
}));
